import { useState } from 'react';

export default function Books() {
  const [showSample, setShowSample] = useState(false);

  return (
    <>
      <section id="books" className="py-16 sm:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-10 sm:mb-16">
            <h2 className="text-4xl sm:text-5xl font-serif text-gray-900 mb-4 sm:mb-6">Published Works</h2>
            <p className="text-base sm:text-xl text-gray-600 max-w-2xl mx-auto">
              Explore transformative narratives that challenge perspectives and inspire personal growth
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 sm:gap-12 items-start">
            {/* Book cover */}
            <div className="relative group max-w-sm mx-auto w-full md:max-w-none">
              <div className="aspect-[3/4] overflow-hidden rounded-lg">
                <img
                  src="https://static.readdy.ai/image/09a869d581523d35fa4e4396fbda86f5/2133e5c400fdc21ca6feb4502d01c8f1.jpeg"
                  alt="21 Days Book Cover"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </div>
            </div>

            {/* Book details */}
            <div className="space-y-6 sm:space-y-8">
              <div>
                <h3 className="text-3xl sm:text-4xl font-serif text-gray-900 mb-3 sm:mb-4">21 Days</h3>
                <p className="text-base sm:text-lg text-gray-600 leading-relaxed mb-4 sm:mb-6">
                  A profound exploration of spiritual awakening and personal transformation. Follow a journey through 21 days of introspection, challenge, and growth that will reshape your understanding of faith, purpose, and the human experience.
                </p>
                <p className="text-base sm:text-lg text-gray-600 leading-relaxed">
                  This compelling narrative weaves together elements of spiritual discovery, philosophical inquiry, and raw human emotion to create a story that resonates long after the final page.
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                <button
                  onClick={() => setShowSample(true)}
                  className="w-full border-2 border-gray-300 text-gray-700 py-3 rounded-lg font-medium hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap text-sm sm:text-base"
                >
                  Read Sample
                </button>
                <a
                  href="https://www.amazon.com/21-Days-Forrest-Jamison-ebook/dp/B0FW7N3R2Y"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full bg-amber-600 text-white py-3 rounded-lg font-medium hover:bg-amber-700 transition-colors cursor-pointer whitespace-nowrap text-center text-sm sm:text-base"
                >
                  Buy on Amazon
                </a>
              </div>

              <div className="grid grid-cols-3 gap-4 sm:gap-6 pt-6 sm:pt-8 border-t border-gray-200">
                <div className="text-center">
                  <div className="text-2xl sm:text-3xl font-bold text-amber-600 mb-1 sm:mb-2">4.8</div>
                  <div className="text-xs sm:text-sm text-gray-600">Rating</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl sm:text-3xl font-bold text-amber-600 mb-1 sm:mb-2">250+</div>
                  <div className="text-xs sm:text-sm text-gray-600">Reviews</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl sm:text-3xl font-bold text-amber-600 mb-1 sm:mb-2">2024</div>
                  <div className="text-xs sm:text-sm text-gray-600">Published</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {showSample && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 sm:p-6">
          <div className="bg-white rounded-xl max-w-3xl w-full max-h-[90vh] overflow-hidden">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-5 sm:px-8 py-4 sm:py-6 flex items-center justify-between">
              <h3 className="text-xl sm:text-2xl font-serif text-gray-900">21 Days — Sample Chapter</h3>
              <button
                onClick={() => setShowSample(false)}
                className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-xl text-gray-600"></i>
              </button>
            </div>

            <div className="px-5 sm:px-8 py-6 sm:py-8 overflow-y-auto max-h-[calc(90vh-80px)]">
              <div className="prose prose-lg max-w-none">
                <h4 className="text-lg sm:text-xl font-serif text-gray-900 mb-4">Chapter One: The Beginning</h4>
                <p className="text-gray-700 leading-relaxed mb-4 text-sm sm:text-base">
                  The morning arrived with an unusual stillness. Not the peaceful kind that invites meditation, but the heavy silence that precedes transformation. I stood at the window of my small apartment, watching the city wake up below, unaware that this day would mark the beginning of everything changing.
                </p>
                <p className="text-gray-700 leading-relaxed mb-4 text-sm sm:text-base">
                  Twenty-one days. That's what the letter said. Twenty-one days to discover what I had been searching for my entire life, or to lose myself completely in the attempt. The paper felt heavy in my hands, though it weighed nothing at all. Sometimes the most profound truths arrive in the simplest packages.
                </p>
                <p className="text-gray-700 leading-relaxed mb-4 text-sm sm:text-base">
                  I had always considered myself a rational person. Faith was something I observed in others with a mixture of curiosity and skepticism. Yet here I was, holding an invitation to a journey I didn't understand, from a source I couldn't identify, feeling an inexplicable pull to accept.
                </p>
                <p className="text-gray-700 leading-relaxed mb-4 text-sm sm:text-base">
                  The instructions were simple: "Begin today. Trust the process. Let go of what you think you know." Simple words that demanded everything.
                </p>
                <p className="text-gray-700 leading-relaxed mb-4 text-sm sm:text-base">
                  I made coffee, the familiar ritual grounding me in the present moment. Steam rose from the cup, dissipating into the air like my certainties about the world. What was I afraid of? Success? Failure? Or perhaps the terrifying possibility that everything I believed about reality was about to be challenged?
                </p>
                <p className="text-gray-700 leading-relaxed mb-4 text-sm sm:text-base">
                  Day one of twenty-one. The journey had already begun, whether I was ready or not. And as I took that first sip of coffee, I made a decision that would change everything: I would say yes to the unknown.
                </p>
                <div className="mt-6 sm:mt-8 pt-5 sm:pt-6 border-t border-gray-200">
                  <p className="text-gray-600 italic text-center text-sm sm:text-base">
                    Continue reading to discover what happens next...
                  </p>
                  <div className="flex justify-center mt-4 sm:mt-6">
                    <a
                      href="https://www.amazon.com/21-Days-Forrest-Jamison-ebook/dp/B0FW7N3R2Y"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bg-amber-600 text-white px-6 sm:px-8 py-3 rounded-lg font-medium hover:bg-amber-700 transition-colors cursor-pointer whitespace-nowrap text-sm sm:text-base"
                    >
                      Get the Full Book
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
